import { createSelector } from "@reduxjs/toolkit";
import envConfig from "../../../utils/env.config";
import { parseJwt } from "../../../utils/help/user/user";
import {
  getWalletTopUpLimits,
  getWalletTopUpOffers,
} from "../../../utils/wallet/walletIncentives";
import type { RootState } from "../app/store";
import { selectSingleScrollHomeScreen } from "./homeScreenSelectors";

export const selectHasWallet = (state: RootState) =>
  !!(
    state.config.restaurantConfig?.hasWallet ??
    state.config.restaurantInfo?.config?.hasWallet
  );

/**
 * The wallet exists only inside the single-scroll home screen
 * (`singleScrollHomeScreen`, prepits/prepareit-front#340) — its UI replaces
 * that layout's points card and has no place in the old tab-bar home. With
 * either flag off, nothing wallet-related renders or fetches.
 */
export const selectWalletEnabled = (state: RootState) =>
  selectHasWallet(state) && selectSingleScrollHomeScreen(state);

/**
 * Whether customers can top up: off in the wallet's inactive state
 * (`walletActive: false`, prepits/project#4259), where the balance can still
 * be spent and the history read. Only an explicit `false` turns it off, so a
 * restaurant whose config predates the flag stays active -- the backend and
 * the app read it the same way.
 */
export const selectCanTopUpWallet = (state: RootState) =>
  selectWalletEnabled(state) &&
  (state.config.restaurantConfig?.walletActive ??
    state.config.restaurantInfo?.config?.walletActive) !== false;

/**
 * The restaurant the wallet calls are made for. A partner branch passes its own
 * id and the backend resolves the shared account, so this is the same id the
 * loyalty calls use (useLoyaltyDataRefetch).
 */
export const selectWalletRestaurantId = (state: RootState): number | null => {
  const id =
    state.config.selectedFranchise?.restaurantId ||
    state.config.restaurantInfo?.id ||
    parseInt(envConfig.restaurantId!, 10);

  return Number.isFinite(id) && id > 0 ? id : null;
};

/**
 * A customer the wallet endpoints will answer for. Visitor tokens are refused
 * (and the backend ignores `useWallet` from them), and a token with no phone
 * number is mid-signup.
 */
export const selectIsWalletCustomer = createSelector(
  [(state: RootState) => state.userInfo.token],
  (token) => {
    const decoded = token ? parseJwt(token) : undefined;

    return (
      !!decoded &&
      !decoded.visitor &&
      !!decoded.customer_id &&
      !!decoded.phone_number
    );
  }
);

/** Summary data only counts for the restaurant it was fetched for. */
const selectCurrentSummary = (state: RootState) => {
  const { summary } = state.wallet;

  return summary.restaurantId === selectWalletRestaurantId(state)
    ? summary.data
    : null;
};

/**
 * No summary means either not loaded yet or no wallet account (the service only
 * opens one on the first top-up); both read as a zero balance.
 */
export const selectWalletBalance = (state: RootState): number =>
  selectCurrentSummary(state)?.availableBalance ?? 0;

export const selectWalletCurrency = (state: RootState): string | null =>
  selectCurrentSummary(state)?.currency ??
  state.wallet.presets?.currency ??
  null;

/**
 * Whether the customer has ever topped up: the wallet service opens a wallet
 * on the first credit, so a loaded summary is the signal (a `null` one means
 * "no wallet", as the app reads it). Only these customers keep an inactive
 * wallet (prepits/project#4259).
 */
const selectHasWalletAccount = (state: RootState) =>
  selectCurrentSummary(state) !== null;

/**
 * The wallet's menu card and side-menu entry (and later the checkout's). An
 * active wallet shows to everyone; an inactive one only to a customer still
 * holding a balance, since there is nothing else to do with it.
 */
export const selectWalletVisible = (state: RootState) =>
  selectCanTopUpWallet(state) ||
  (selectWalletEnabled(state) &&
    selectHasWalletAccount(state) &&
    selectWalletBalance(state) > 0);

/**
 * An inactive wallet spent down to zero: nothing to top up or spend, so only
 * its history stays reachable ("Wallet history" in the side menu).
 */
export const selectWalletHistoryOnly = (state: RootState) =>
  selectWalletEnabled(state) &&
  !selectCanTopUpWallet(state) &&
  selectHasWalletAccount(state) &&
  selectWalletBalance(state) <= 0;

// ts-prune-ignore-next -- used by the Wallet screens (#4263)
export const selectWalletSummaryStatus = (state: RootState) =>
  state.wallet.summary.status;

/**
 * Not scoped to the restaurant, unlike the balance: presets belong to the
 * account, which a whole partner group shares, so the ones baked for the
 * storefront hold whichever branch the guest is on.
 */
const selectWalletPresets = (state: RootState) => state.wallet.presets;

export const selectWalletTopUpTiers = createSelector(
  [selectWalletPresets],
  (presets) => presets?.tiers ?? []
);

/** The preset cards: the merchant's own amounts, or one per tier. */
export const selectWalletTopUpOffers = createSelector(
  [selectWalletPresets],
  getWalletTopUpOffers
);

export const selectWalletTopUpLimits = createSelector(
  [selectWalletPresets],
  getWalletTopUpLimits
);

export const selectLatestWalletTransactions = createSelector(
  [
    (state: RootState) => state.wallet.latestTransactions,
    selectWalletRestaurantId,
  ],
  (latest, restaurantId) =>
    latest.restaurantId === restaurantId ? latest.data ?? [] : []
);

/**
 * What the wallet paid of the order on the pay screen: `amountPaidWithWallet`
 * on the receipt, which arrives as a decimal string.
 */
export const selectReceiptWalletAmount = (state: RootState): number => {
  const amount = Number(state.receipt?.payScreenReceipt?.amountPaidWithWallet);

  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};
